import React, { useEffect, useState } from 'react'
import { useBenefits } from '../BenefitsProvider'
import {
  TextField,
  InputField,
  SelectField,
} from '~/components/shared/FormFields'
import { Button } from '~/lib/ui/button'
import { useTranslation } from '~/hooks/useTranslation'
import ResumeUpload from './ResumeUpload'
import { Form, FormLabel } from '~/lib/ui/form'
import SearchDropdown from '~/components/job_search/RecruiterSearchPage/SearchDropdown'
import { cn } from '~/lib/utils'

const OrientationCallForm = () => {
  const { t } = useTranslation()
  const { control, message, submitted, select } = useBenefits()
  const disabled = !message || submitted
  return (
    <ServiceRequestForm>
      <TextField
        control={control}
        name="message"
        label={t('schedule_modal.coach_introduction.message_label')}
      />
      <div className="flex justify-center">
        {/* Tooltip wrapper */}
        <div
          className="relative ml-auto"
          data-toggle="tooltip"
          data-placement="top"
          title={!message ? t('schedule_modal.complete_form_tooltip') : ''}
        >
          <Button
            variant={select ? 'default' : 'coachcta'}
            disabled={disabled} // Disable the button if the message is empty
          >
            {t('schedule_modal.next_button')}
          </Button>
        </div>
      </div>
    </ServiceRequestForm>
  )
}

const CallForm = ({
  formDisabled,
  electronic,
  schedule = true,
  scheduleCallCta,
  submitCta,
  children,
}) => {
  const { t } = useTranslation('call_form')
  const { message, control, submitted, select, createServiceRequest } =
    useBenefits()
  const disabled = !message || submitted || formDisabled
  return (
    <ServiceRequestForm>
      <TextField
        control={control}
        name="message"
        label={t('notes_description')}
      />
      {children}
      <div
        className={cn(
          'flex justify-center',
          schedule && electronic && 'flex-col items-center gap-4',
        )}
      >
        {/* Tooltip wrapper */}
        {electronic && (
          <div
            className="relative"
            data-toggle="tooltip"
            data-placement="top"
            title={disabled ? t('complete_form_tooltip') : ''}
          >
            <Button
              type="button"
              onClick={createServiceRequest}
              variant={select ? 'default' : 'coachcta'}
              disabled={disabled}
            >
              {submitCta}
            </Button>
          </div>
        )}
        {schedule && electronic && <div className="font-medium">{t('or')}</div>}
        {schedule && (
          <div
            className="relative"
            data-toggle="tooltip"
            data-placement="top"
            title={disabled ? t('complete_form_tooltip') : ''}
          >
            <Button
              variant={select ? 'default' : 'coachcta'}
              disabled={disabled}
            >
              {scheduleCallCta || t('next_button')}
            </Button>
          </div>
        )}
      </div>
    </ServiceRequestForm>
  )
}

const LinkedInReviewForm = () => {
  const { t } = useTranslation()
  const { message, control, linkedinUrl, submitted, select } = useBenefits()
  const disabled = !(message && linkedinUrl) || submitted

  return (
    <ServiceRequestForm>
      <InputField
        control={control}
        name="linkedin_url"
        label={t('schedule_modal.linkedin_optimization.linkedin_url_label')}
      />
      <TextField
        control={control}
        name="message"
        label={t('schedule_modal.linkedin_optimization.message_label')}
      />
      <div className="flex flex-col items-center justify-center gap-4">
        {/* Tooltip wrapper */}
        <div
          className="relative"
          data-toggle="tooltip"
          data-placement="top"
          title={
            !(message && linkedinUrl)
              ? t('schedule_modal.complete_form_tooltip')
              : ''
          }
        ></div>
        <div
          className="relative"
          data-toggle="tooltip"
          data-placement="top"
          title={
            !(message && linkedinUrl)
              ? t('schedule_modal.complete_form_tooltip')
              : ''
          }
        >
          <Button
            variant={select ? 'default' : 'coachcta'}
            disabled={disabled} // Disable the button if the message is empty
          >
            {t('schedule_modal.schedule_call')}
          </Button>
        </div>
      </div>
    </ServiceRequestForm>
  )
}

const ResumeReviewForm = () => {
  const { t } = useTranslation('schedule_modal.resume_review')
  const [fileType, setFileType] = useState('upload')
  const {
    control,
    message,
    createServiceRequest,
    meetingViaCalendly,
    seniorityLevel,
    current_user,
    coach,
    setUploadedResumeId,
    resumeReview,
    createRevisionRequest,
    notes,
    resumeId,
    uploadedResumeId,
    remote_content,
    serviceRequestForm,
    submitted,
    select,
  } = useBenefits()

  const revisionRequest = resumeReview !== null

  const variant = select ? 'default' : 'coachcta'

  return (
    <ServiceRequestForm>
      {revisionRequest ? (
        <p>{t('intro_text_revision')}</p>
      ) : (
        <p>{t('intro_text')}</p>
      )}
      {!revisionRequest && (
        <div className="flex items-center justify-between">
          <Button
            type="button"
            variant={fileType == 'upload' ? variant : 'outline'}
            onClick={() => {
              setFileType('upload')
            }}
          >
            {t('upload_resume')}
          </Button>
          <div className="subtitle">{t('or')}</div>
          <Button
            type="button"
            variant={fileType == 'select' ? 'coachcta' : 'outline'}
            onClick={() => {
              setFileType('select')
            }}
            disabled={current_user.candidate_resumes.length == 0} // Disable the button if there are no resumes
          >
            {t('choose_resume')}
          </Button>
        </div>
      )}
      {fileType == 'upload' ? (
        <ResumeUpload
          containerClassName="px-0 pb-0"
          resumeReview={true}
          setUploadedResumeId={setUploadedResumeId}
          revisionRequest={revisionRequest}
          serviceRequestForm={serviceRequestForm}
        />
      ) : (
        <SelectField
          control={control}
          name="resume_document_id"
          label={t('select_resume_label')}
          choices={current_user.candidate_resumes}
        />
      )}
      {!revisionRequest && (
        <SelectField
          control={control}
          name="seniority_level"
          label={t('seniority_level_label')}
          choices={[
            {
              value: 'Entry to Mid Level',
              label: t('entry_level'),
            },
            {
              value: 'Manager to Director Level',
              label: t('manager_level'),
            },
            {
              value: 'VP to C Level',
              label: t('vp_level'),
            },
          ]}
        />
      )}
      <TextField
        control={control}
        name={revisionRequest ? 'notes' : 'message'}
        label={t('message_label')}
      />
      {!revisionRequest && (
        <>
          <hr />
          <div className="subtitle">{t('example_jobs')}</div>
          <p>{t('example_jobs_description')}</p>
          <InputField
            control={control}
            name="example_job_1"
            label={t('example_job_1_label')}
          />
          <InputField
            control={control}
            name="example_job_2"
            label={t('example_job_2_label')}
          />
          <InputField
            control={control}
            name="example_job_3"
            label={t('example_job_3_label')}
          />
        </>
      )}
      <div className="flex justify-center">
        {/* Tooltip wrapper */}
        <div
          className="relative"
          data-toggle="tooltip"
          data-placement="top"
          title={!message ? t('complete_form_tooltip') : ''}
        >
          {revisionRequest ? (
            <div className="flex flex-col items-center justify-center gap-4">
              {/* Tooltip wrapper */}
              <div
                className="relative"
                data-toggle="tooltip"
                data-placement="top"
              >
                <Button
                  type="button"
                  onClick={createServiceRequest}
                  variant={select ? 'default' : 'coachcta'}
                  disabled={
                    revisionRequest
                      ? !(notes && remote_content) || submitted
                      : !(message && seniorityLevel) || submitted
                  } // Disable th
                >
                  {t('request_final_review')}
                </Button>
              </div>
              <div className="font-medium">
                {t('schedule_modal.linkedin_optimization.or')}
              </div>
              <div
                className="relative"
                data-toggle="tooltip"
                data-placement="top"
              >
                <Button
                  variant={select ? 'default' : 'coachcta'}
                  disabled={!(notes && remote_content) || submitted}
                >
                  {t('schedule_modal.schedule_call')}
                </Button>
              </div>
            </div>
          ) : !meetingViaCalendly || !coach || coach.isLiaison ? (
            <Button
              type="button"
              onClick={
                revisionRequest ? createRevisionRequest : createServiceRequest
              }
              variant={select ? 'default' : 'coachcta'}
              disabled={!(message && seniorityLevel) || submitted} // Disable the button if the message is empty
            >
              {t('request_review')}
            </Button>
          ) : (
            <Button
              variant={select ? 'default' : 'coachcta'}
              disabled={
                (!message &&
                  !(uploadedResumeId || resumeId) &&
                  !seniorityLevel) ||
                submitted
              } // Disable the button if the message is empty
            >
              {t('next_button')}
            </Button>
          )}
        </div>
      </div>
    </ServiceRequestForm>
  )
}

const ExecutiveBioForm = () => {
  const { t } = useTranslation('executive_bio_call_form')
  const {
    control,
    setUploadedResumeId,
    serviceRequestForm,
    linkedinUrl,
    uploadedResumeId,
  } = useBenefits()
  const disabled = !linkedinUrl || !uploadedResumeId
  return (
    <CallForm formDisabled={disabled}>
      <InputField
        control={control}
        name="linkedin_url"
        label={t('linkedin_url_label')}
      />

      <ResumeUpload
        containerClassName="px-0 pb-0"
        resumeReview={true}
        setUploadedResumeId={setUploadedResumeId}
        serviceRequestForm={serviceRequestForm}
      />
    </CallForm>
  )
}

const TargetCompanyResearchForm = () => {
  const { t } = useTranslation('target_company_research_form')
  const {
    control,
    industries,
    revenue_options,
    company_sizes,
    publicOrPrivate,
    exampleTitle1,
    exampleTitle2,
    revenueRange,
    companySize,
    location,
    industry,
  } = useBenefits()
  const companySizeChoices = Object.keys(company_sizes)
  const revenueChoices = Object.keys(revenue_options)
  const noJob = !exampleTitle1 && !exampleTitle2
  const disabled =
    !industry ||
    !publicOrPrivate ||
    noJob ||
    !revenueRange ||
    !companySize ||
    !location

  useEffect(() => {
    if (
      typeof window.initTypeahead === 'function' &&
      typeof Bloodhound === 'function'
    ) {
      window.initTypeahead()
    }
  }, [])
  return (
    <CallForm
      formDisabled={disabled}
      electronic={true}
      schedule={false}
      submitCta={t('submit_request')}
    >
      <SearchDropdown
        control={control}
        name="industry"
        label={t('industry_label')}
        options={industries}
        showLabel={true}
      ></SearchDropdown>
      <SelectField
        control={control}
        name="public_or_private"
        label={t('public_or_private_label')}
        choices={[
          { value: 'Public', label: t('public') },
          { value: 'Private', label: t('private') },
        ]}
      />
      <div>
        <FormLabel>{t('example_title_label')}</FormLabel>
        <InputField control={control} name="example_job_1" />
        <InputField control={control} name="example_job_2" />
      </div>
      <SearchDropdown
        control={control}
        name="revenue_range"
        label={t('revenue_range_label')}
        options={revenueChoices}
        showLabel={true}
      ></SearchDropdown>
      <SearchDropdown
        control={control}
        name="company_size"
        label={t('company_size_label')}
        options={companySizeChoices}
        showLabel={true}
      ></SearchDropdown>
      <InputField
        control={control}
        name="location"
        label={t('location_label')}
        data-source="locations"
        inputClassName={'locations-typeahead'}
      />
    </CallForm>
  )
}

const RecruiterTargetingForm = () => {
  const { t } = useTranslation('recruiter_targeting_form')
  const { control, industries, industry_role, location, industry, salary } =
    useBenefits()
  const salaries = t('salary_ranges', {
    returnObjects: true,
  })

  const disabled = !industry_role || !location || !industry || !salary

  useEffect(() => {
    if (
      typeof window.initTypeahead === 'function' &&
      typeof Bloodhound === 'function'
    ) {
      window.initTypeahead()
    }
  }, [])

  return (
    <CallForm
      formDisabled={disabled}
      electronic={true}
      submitCta={t('submit_request')}
      scheduleCallCta={t('schedule_call')}
    >
      <InputField
        control={control}
        name="industry_role"
        label={t('role_label')}
        data-source="roles"
        inputClassName={'roles-typeahead'}
      />
      <InputField
        control={control}
        name="location"
        label={t('location_label')}
        data-source="locations"
        inputClassName={'locations-typeahead'}
      />
      <SearchDropdown
        control={control}
        name="industry"
        label={t('industry_label')}
        options={industries}
        showLabel={true}
      ></SearchDropdown>
      <SearchDropdown
        control={control}
        name="salary"
        label={t('salary_range_label')}
        options={salaries}
        showLabel={true}
      ></SearchDropdown>
    </CallForm>
  )
}

const ServiceRequestForm = ({ children }) => {
  const { serviceRequestForm, onSubmit } = useBenefits()
  return (
    <Form {...serviceRequestForm}>
      <form
        onSubmit={serviceRequestForm.handleSubmit(onSubmit)}
        className="flex flex-col gap-6"
      >
        {children}
      </form>
    </Form>
  )
}

export {
  OrientationCallForm,
  LinkedInReviewForm,
  ResumeReviewForm,
  CallForm,
  ExecutiveBioForm,
  RecruiterTargetingForm,
  TargetCompanyResearchForm,
  ServiceRequestForm,
}
